/* Built In Imports */
import { useState } from 'react';

/* External Imports */
import {
  Box,
  Flex,
  SimpleGrid,
  ListItem,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { nanoid } from 'nanoid';
import { BsArrowRightCircle } from 'react-icons/bs';
import { useMediaQuery } from 'react-responsive';
import Slider from 'react-slick';

/* Internal Imports */
/* Components */
import SectionTitle from '@app-ila/components/Text/SectionTitle';
import StructuredTextParser from '@app-ila/components/Utility/StructuredTextParser';

/* Styles */
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

/**
 * Renders HMMAbout component
 * @param {Object}
 * @returns {ReactElement}
 */
const HMMAbout = ({ sectionContent }) => {
  const [activeTab, setActiveTab] = useState(0);
  const isSmallScreen = useMediaQuery({ maxWidth: 834 });

  const sliderDotSettings = {
    base: 1,
    md: 1,
  };

  const settings = {
    dots: true,
    // arrows: false,
    infinite: false,
    autoplay: false,
    lazyLoad: false,
    slidesToShow: 3.2,
    slidesToScroll: 1,
    initialSlide: 0,
    centerPadding: 0,
    appendDots: dots => (
      <Flex
        height="auto"
        bottom={{ base: '-50px', md: '-50px' }}
        overflow={{ base: 'hidden', lg: 'auto' }}
      >
        <Flex
          marginRight="auto"
          marginLeft="auto"
          bgColor="#0E0E6B"
          //width="108px"
          height="14px"
          borderRadius="9px"
          justifyContent="space-between"
          className="Glimpses appendDots"
          marginTop="3em"
        >
          <UnorderedList m="0" display="flex">
            {/* {dots}{' '} */}
            {dots.map((dot, index) => {
              const button = dot.props.children;
              return (
                <ListItem
                  onClick={e => {
                    button.props.onClick(e);
                  }}
                  key={index}
                  width="19px !important"
                  height="13px !important"
                  borderRadius="9px !important"
                  background={
                    Math.ceil(
                      activeTab /
                        sliderDotSettings[isSmallScreen ? 'base' : 'md']
                    ) === parseInt(dot.key)
                      ? '#F27123'
                      : '#0E0E6B'
                  }
                  margin="0 !important"
                />
              );
            })}
          </UnorderedList>
        </Flex>
      </Flex>
    ),
    responsive: [
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1.75,
          slidesToScroll: 1,
          initialSlide: 0,
        },
      },
    ],
  };

  const AboutCards = ({ cards, devocefrm }) => {
    return (
      <Box maxW="1170px" mx="auto" width="100%" className={devocefrm}>
        <Flex columns={{ base: 1, sm: 1, md: 1 }} spacingY="20px">
          {cards?.map((secCon, index) => {
            const margin = (index + 1) % 2 === 0 ? '0px' : 0;
            return (
              <Box
                textColor="#fff"
                pos="relative"
                key={nanoid()}
                w={{ base: '100%', sm: '100%', md: '100%', lg: '226px' }}
                bgRepeat="no-repeat"
                mt={{
                  base: '0',
                  xl: margin,
                }}
                top={index % 2 === 1 ? '40px' : '0'}
                ml="-2px"
                mr="-2px"
              >
                <Box
                  display="flex"
                  alignItems="flex-start"
                  background={'#8686CF33'}
                  minH={'168px'}
                >
                  <Box
                    fontSize="16px"
                    lineHeight="160%"
                    fontWeight={400}
                    letterSpacing="0em"
                    textAlign="center"
                    p="30px 16px"
                  >
                    <StructuredTextParser
                      str={render(secCon.description)}
                      region="in"
                      lang="en"
                      mt="0"
                      color="#ffffff"
                      textAlign="center"
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Flex>
      </Box>
    );
  };

  const AboutMobileCards = ({ cards, devocefrm }) => {
    return (
      <Box maxW="1170px" mx="auto" width="100%" className={devocefrm}>
        <Slider
          {...settings}
          beforeChange={(old, newChange) => {
            setActiveTab(newChange);
          }}
        >
          {cards?.map((secCon, index) => {
            const margin = (index + 1) % 2 === 0 ? '0px' : 0;
            return (
              <Flex
                flexDirection="column"
                maxW={{ base: '200.513px', lg: '355px' }}
                textAlign="center"
              >
                <Box
                  textColor="#fff"
                  pos="relative"
                  key={nanoid()}
                  w={{ base: '100%', sm: '100%', md: '100%', lg: '226px' }}
                  bgRepeat="no-repeat"
                  mt={{
                    base: '0',
                    xl: margin,
                  }}
                  top={index % 2 === 1 ? '40px' : '0'}
                  ml="-2px"
                  mr="-2px"
                >
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    background={'#8686CF33'}
                    minH={'168px'}
                  >
                    <Box
                      fontSize="16px"
                      lineHeight="160%"
                      fontWeight={400}
                      letterSpacing="0em"
                      textAlign="center"
                      p="30px 16px"
                    >
                      <StructuredTextParser
                        str={render(secCon.description)}
                        region="in"
                        lang="en"
                        mt="0"
                        color="#ffffff"
                        textAlign="center"
                      />
                    </Box>
                  </Box>
                </Box>
              </Flex>
            );
          })}
        </Slider>
        <style global jsx>
          {`
            @media only screen and (max-width: 834px) {
              .mobile .slick-slide {
                width: 200px !important;
              }
              .mobile .slick-prev,
              .mobile .slick-next {
                display: none !important;
              }
            }
          `}
        </style>
      </Box>
    );
  };

  return (
    <Box
      py={{ base: '30px', md: '40px', xl: '60px' }}
      background={
        'linear-gradient(180deg, rgba(7, 7, 191, 0.42) 0%, rgba(7, 7, 192, 0) 100%)'
      }
    >
      <Box p={{ base: '16px', lg: '0' }}>
        <Flex
          flexDirection="column"
          position="relative"
          mb="20px"
          pt="60px"
          m="0 auto"
          maxW={'1170px'}
          className="abouttitle"
        >
          <SectionTitle titleObj={sectionContent[0]} />
          <Box
            color="#FFF"
            fontSize="20px"
            fontStyle="normal"
            fontWeight="400"
            lineHeight="32px"
            fontFamily="var(--FedraSansStd-book), sans-serif"
            mx="auto"
            m="0"
            width={{ base: '100%', lg: '500px' }}
          >
            <StructuredTextParser
              str={render(sectionContent?.[1]?.body)}
              fontSize={{ base: '18px', lg: '26px' }}
              color="#FFF"
              fontStyle="normal"
              mt={{ md: '10px', base: '10px' }}
              mb={{ md: '60px', base: '60px' }}
              fontWeight="400"
              lineHeight="32px"
              maxW="100%"
              fontFamily="var(--FedraSansStd-book), sans-serif"
            />
          </Box>
        </Flex>
        {isSmallScreen ? (
          <>
            {/* <AboutMobileCards
              cards={sectionContent[2].cards}
              devocefrm="mobile"
            /> */}
            <Box maxW="1170px" mx="auto" width="100%" className={'mobile'}>
              <Slider
                {...settings}
                beforeChange={(old, newChange) => {
                  setActiveTab(newChange);
                }}
              >
                {sectionContent[2].cards?.map((secCon, index) => {
                  const margin = (index + 1) % 2 === 0 ? '0px' : 0;
                  return (
                    <Flex
                      flexDirection="column"
                      maxW={{ base: '199px', lg: '355px' }}
                      textAlign="center"
                    >
                      <Box
                        textColor="#fff"
                        pos="relative"
                        key={nanoid()}
                        w={{
                          base: '100%',
                          sm: '100%',
                          md: '100%',
                          lg: '226px',
                        }}
                        mt={{
                          base: '0',
                          xl: margin,
                        }}
                        top={index % 2 === 1 ? '40px' : '0'}
                        ml="-12px"
                        mr="-6px"
                        background={'#8686CF33'}
                      >
                        <Box
                          display="flex"
                          alignItems="flex-start"
                          // minH={'168px'}
                        >
                          <Box
                            fontSize="16px"
                            lineHeight="160%"
                            fontWeight={400}
                            letterSpacing="0em"
                            textAlign="center"
                            p="30px 16px"
                          >
                            <StructuredTextParser
                              str={render(secCon.description)}
                              region="in"
                              lang="en"
                              mt="0"
                              color="#ffffff"
                              textAlign="center"
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Flex>
                  );
                })}
              </Slider>
              <style global jsx>
                {`
                  .abouttitle div h3 {
                    line-height: 55px !important;
                  }
                  @media only screen and (max-width: 834px) {
                    .mobile .slick-slide {
                      width: 195px !important;
                      margin-left: -2px;
                      margin-right: -2px;
                    }
                    .mobile .slick-prev,
                    .mobile .slick-next {
                      display: none !important;
                    }
                  }
                `}
              </style>
            </Box>
          </>
        ) : (
          <>
            <AboutCards cards={sectionContent[2].cards} devocefrm="desktop" />
          </>
        )}
      </Box>
    </Box>
  );
};

export default HMMAbout;
